import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Spinner from '@cloudscape-design/components/spinner'
import { useMutation } from '@apollo/client/react'
import { useState } from 'react'
import { useClient } from '@/hooks/useClients'
import type { ClientConfig } from '@/lib/types'
import { TRIGGER_AUTO_RUN } from '@/lib/operations/clients'
import { formatDate } from '@/utils/format'

interface Props {
  clientId: string
  onEditStep: (stepIndex: number) => void
}

const NOT_SET = <Box color="text-status-error">
  Not configured
</Box>

function renderText(value: string | null | undefined): string | React.ReactNode {
  return value && value.length > 0 ? value : NOT_SET
}

function renderList(value: readonly string[] | null | undefined): string | React.ReactNode {
  return value && value.length > 0 ? value.join(', ') : NOT_SET
}

function renderBool(value: boolean | null | undefined): string | React.ReactNode {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return NOT_SET
}

function renderDays(value: readonly number[] | null | undefined): string | React.ReactNode {
  if (!value || value.length === 0) return NOT_SET
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  return value
    .slice()
    .sort((a, b) => a - b)
    .map(d => names[d] ?? String(d))
    .join(', ')
}

// Format an ISO datetime for display, falling back to `placeholder` when the
// value is null / undefined / empty. Uses the shared `formatDate` helper so
// date formatting stays consistent across the app.
function renderDateTime(
  value: string | null | undefined,
  placeholder: string,
): string {
  if (!value) return placeholder
  return formatDate(value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// Determines whether a section has *any* meaningful data. When false, the
// section's action button renders as "Set up" instead of "Edit".
function isBusinessInfoEmpty(c: ClientConfig): boolean {
  return (
    !(c.brandName ?? c.brand_name) &&
    !(c.brandSpecialization ?? c.brand_specialization) &&
    !c.industry &&
    !(c.websiteUrl ?? c.website_url) &&
    !(c.brandLocation ?? c.brand_location) &&
    !c.marketingProfile?.phone &&
    !c.capabilities?.serviceArea
  )
}

function isOnlinePresenceEmpty(c: ClientConfig): boolean {
  const mp = c.marketingProfile
  return !mp?.facebookUrl && !mp?.linkedinUrl && !mp?.twitterUrl && !mp?.ctaUrl
}

function isServicesEmpty(c: ClientConfig): boolean {
  const cap = c.capabilities
  return (
    !cap?.businessDescription &&
    !(cap?.productCategories && cap.productCategories.length > 0) &&
    !(cap?.servicesOffered && cap.servicesOffered.length > 0) &&
    !cap?.deliveryTimeline &&
    !(cap?.acceptedInsurance && cap.acceptedInsurance.length > 0) &&
    !(cap?.ehrIntegrations && cap.ehrIntegrations.length > 0)
  )
}

function isCampaignEmpty(c: ClientConfig): boolean {
  const selected = c.selectedContentTypes ?? c.selected_content_types ?? []
  return !c.marketingProfile?.campaignGoal && selected.length === 0
}

function isScheduleEmpty(c: ClientConfig): boolean {
  return !c.schedulingConfig?.autoGenerate
}

// Label for the section's action button — "Set up" when empty, "Edit" otherwise.
function actionLabel(empty: boolean): string {
  return empty ? 'Set up' : 'Edit'
}

// Disambiguates identically-labelled buttons across sections for accessibility
// and test stability (e.g. "Edit Business Info" vs "Edit Campaign Setup").
function sectionAriaLabel(section: string, empty: boolean): string {
  return `${actionLabel(empty)} ${section}`
}

export default function ConfigureSummary({ clientId, onEditStep }: Props) {
  const { client, loading } = useClient(clientId)
  const [triggerAutoRun, { loading: triggering }] = useMutation(TRIGGER_AUTO_RUN)
  const [triggerResult, setTriggerResult] = useState<'success' | 'error' | null>(null)

  if (loading && !client) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    )
  }

  if (!client) {
    return (
      <Box textAlign="center" padding="xxl" color="text-status-inactive">
        Client not found.
      </Box>
    )
  }

  const businessEmpty = isBusinessInfoEmpty(client)
  const onlineEmpty = isOnlinePresenceEmpty(client)
  const servicesEmpty = isServicesEmpty(client)
  const campaignEmpty = isCampaignEmpty(client)
  const scheduleEmpty = isScheduleEmpty(client)

  const marketingProfile = client.marketingProfile
  const capabilities = client.capabilities
  const contentInclusion = client.contentInclusion
  const autoGenerate = client.schedulingConfig?.autoGenerate
  const selectedContentTypes = client.selectedContentTypes ?? client.selected_content_types ?? []

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button
                onClick={() => onEditStep(0)}
                ariaLabel={sectionAriaLabel('Business Info', businessEmpty)}
              >
                {actionLabel(businessEmpty)}
              </Button>
            }
          >
            Business Info
          </Header>
        }
      >
        <KeyValuePairs
          columns={2}
          items={[
            { label: 'Brand name', value: renderText(client.brandName ?? client.brand_name) },
            { label: 'Brand specialization', value: renderText(client.brandSpecialization ?? client.brand_specialization) },
            { label: 'Industry', value: renderText(client.industry) },
            { label: 'Website URL', value: renderText(client.websiteUrl ?? client.website_url) },
            { label: 'Brand location', value: renderText(client.brandLocation ?? client.brand_location) },
            { label: 'Phone', value: renderText(marketingProfile?.phone) },
            { label: 'Service area', value: renderText(capabilities?.serviceArea) },
          ]}
        />
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button
                onClick={() => onEditStep(1)}
                ariaLabel={sectionAriaLabel('Online Presence', onlineEmpty)}
              >
                {actionLabel(onlineEmpty)}
              </Button>
            }
          >
            Online Presence
          </Header>
        }
      >
        <KeyValuePairs
          columns={2}
          items={[
            { label: 'Facebook URL', value: renderText(marketingProfile?.facebookUrl) },
            { label: 'LinkedIn URL', value: renderText(marketingProfile?.linkedinUrl) },
            { label: 'Twitter/X URL', value: renderText(marketingProfile?.twitterUrl) },
            { label: 'Call-to-action URL', value: renderText(marketingProfile?.ctaUrl) },
          ]}
        />
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button
                onClick={() => onEditStep(2)}
                ariaLabel={sectionAriaLabel('Services & Insurance', servicesEmpty)}
              >
                {actionLabel(servicesEmpty)}
              </Button>
            }
          >
            Services & Insurance
          </Header>
        }
      >
        <KeyValuePairs
          columns={2}
          items={[
            { label: 'Business description', value: renderText(capabilities?.businessDescription) },
            { label: 'Delivery timeline', value: renderText(capabilities?.deliveryTimeline) },
            { label: 'Product categories', value: renderList(capabilities?.productCategories) },
            { label: 'Services offered', value: renderList(capabilities?.servicesOffered) },
            { label: 'Accepted insurance', value: renderList(capabilities?.acceptedInsurance) },
            { label: 'EHR integrations', value: renderList(capabilities?.ehrIntegrations) },
          ]}
        />
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button
                onClick={() => onEditStep(3)}
                ariaLabel={sectionAriaLabel('Campaign Setup', campaignEmpty)}
              >
                {actionLabel(campaignEmpty)}
              </Button>
            }
          >
            Campaign Setup
          </Header>
        }
      >
        <KeyValuePairs
          columns={2}
          items={[
            { label: 'Campaign goal', value: renderText(marketingProfile?.campaignGoal) },
            { label: 'Selected content types', value: renderList(selectedContentTypes) },
            {
              label: 'Daily Budget Cap (USD)',
              value: client.budgetLimitDailyUsd != null
                ? `$${client.budgetLimitDailyUsd}`
                : NOT_SET,
            },
            { label: 'Include address', value: renderBool(contentInclusion?.includeAddress) },
            { label: 'Include phone', value: renderBool(contentInclusion?.includePhone) },
            { label: 'Include email', value: renderBool(contentInclusion?.includeEmail) },
            { label: 'Include website', value: renderBool(contentInclusion?.includeWebsite) },
            { label: 'Include pricing', value: renderBool(contentInclusion?.includePricing) },
            { label: 'Include staff names', value: renderBool(contentInclusion?.includeStaffNames) },
          ]}
        />
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  onClick={async () => {
                    setTriggerResult(null)
                    try {
                      await triggerAutoRun({ variables: { clientId } })
                      setTriggerResult('success')
                    } catch {
                      setTriggerResult('error')
                    }
                  }}
                  loading={triggering}
                  variant="normal"
                  iconName="play"
                >
                  Trigger run
                </Button>
                <Button
                  onClick={() => onEditStep(4)}
                  ariaLabel={sectionAriaLabel('Auto-generation schedule', scheduleEmpty)}
                >
                  {actionLabel(scheduleEmpty)}
                </Button>
              </SpaceBetween>
            }
          >
            Auto-generation schedule
          </Header>
        }
      >
        <SpaceBetween size="m">
          <KeyValuePairs
            columns={2}
            items={[
              { label: 'Enabled', value: renderBool(autoGenerate?.enabled) },
              { label: 'Content types', value: renderList(autoGenerate?.contentTypes) },
              { label: 'Cadence', value: renderText(autoGenerate?.cadence) },
              { label: 'Time', value: renderText(autoGenerate?.timeUtc) },
              { label: 'Days of week', value: renderDays(autoGenerate?.daysOfWeek) },
              { label: 'Cron expression', value: renderText(autoGenerate?.cronExpr) },
              { label: 'Timezone', value: renderText(autoGenerate?.timezone) },
              { label: 'Notify on queue', value: renderBool(client.schedulingConfig?.notifyOnQueue) },
              { label: 'Last run', value: renderDateTime(client.schedulingConfig?.lastAutoRunAt, 'Never') },
              { label: 'Next run', value: renderDateTime(client.schedulingConfig?.nextAutoRunAt, '—') },
            ]}
          />
          {triggerResult === 'success' && (
            <Box color="text-status-success">Run triggered successfully.</Box>
          )}
          {triggerResult === 'error' && (
            <Box color="text-status-error">Failed to trigger run. Check logs.</Box>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  )
}

export {
  isBusinessInfoEmpty,
  isOnlinePresenceEmpty,
  isServicesEmpty,
  isCampaignEmpty,
  isScheduleEmpty,
}
